/**
 * Learning Profile Types
 * Core types for the Learning Intelligence system
 */

import type { TimeAvailability } from './study-plan';

// Status options for current academic status
export type CurrentStatus = '1L' | '2L' | '3L' | 'bar_prep' | 'attorney' | 'llm' | 'other' | 'unknown';

// Goal mode options
export type GoalMode = 'cold_calls' | 'finals' | 'bar' | 'writing_improvement' | 'fundamentals' | 'general';

// Feedback tone preferences
export type FeedbackTone = 'direct' | 'encouraging' | 'strict' | 'balanced';

// Journey stage (system-inferred)
export type JourneyStage = 'semester_learning' | 'finals_prep' | 'bar_prep' | 'post_bar' | 'unknown';

// Language mode for content adaptation
export type LanguageMode = 'law_school' | 'bar' | 'mixed';

// School calendar type
export type SchoolCalendar = 'semester' | 'quarter';

// Bar attempt type
export type BarAttempt = 'first' | 'retaker' | null;

// Core learning profile from database
// Note: Nullability matches database.generated.ts to prevent type mismatches
export interface LearningProfile {
  id: string;
  user_id: string;

  // Journey Stage (user-declared + system-validated)
  current_status: CurrentStatus;
  school_calendar: SchoolCalendar | null;
  expected_exam_date: string | null;
  bar_exam_date: string | null;
  target_bar_state: string | null;
  bar_attempt: BarAttempt;
  journey_context: string | null;

  // System-Inferred Journey
  journey_stage: JourneyStage | null;
  language_mode: LanguageMode | null;
  journey_stage_confidence: number | null;
  journey_stage_last_updated: string | null;

  // Preferences (user-declared)
  goal_mode: GoalMode | null;
  feedback_tone: FeedbackTone | null;
  avg_session_length_minutes: number | null;

  // Engagement Metrics (system-tracked)
  total_sessions: number | null;
  last_active_at: string | null;
  streak_current: number | null;
  streak_longest: number | null;

  // Profile Health
  profile_completeness: number | null;
  onboarding_completed: boolean | null;
  onboarding_skipped: boolean | null;

  // Intelligence Summary (synced from profile_beliefs)
  strengths: string[];
  weaknesses: string[];
  last_belief_sync: string | null;

  // Study Plan Time Availability
  time_availability: TimeAvailability | null;

  // Timestamps
  created_at: string | null;
  updated_at: string | null;
}

// Profile course entry
export interface ProfileCourse {
  id: string;
  profile_id: string;
  subject: string;
  subject_id: string | null;
  course_name: string | null;
  source: 'declared' | 'inferred' | 'confirmed';
  confidence: number;
  semester: string | null;
  is_current: boolean;
  first_activity_at: string | null;
  last_activity_at: string | null;
  activity_count: number;
  created_at: string;
  updated_at: string;
  // Joined from subjects table when subject_id is set
  linked_subject_name?: string;
}

// Standard legal subjects
export const LEGAL_SUBJECTS = [
  { value: 'contracts', label: 'Contracts' },
  { value: 'torts', label: 'Torts' },
  { value: 'civ_pro', label: 'Civil Procedure' },
  { value: 'property', label: 'Property' },
  { value: 'crim_law', label: 'Criminal Law' },
  { value: 'crim_pro', label: 'Criminal Procedure' },
  { value: 'con_law', label: 'Constitutional Law' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'wills_trusts', label: 'Wills & Trusts' },
  { value: 'family_law', label: 'Family Law' },
  { value: 'corporations', label: 'Corporations' },
  { value: 'admin_law', label: 'Administrative Law' },
  { value: 'secured_trans', label: 'Secured Transactions' },
  { value: 'conflict_laws', label: 'Conflict of Laws' },
  { value: 'legal_writing', label: 'Legal Writing' },
  { value: 'prof_resp', label: 'Professional Responsibility' },
  // Additional subjects (originally Issue Spotter-specific, now official)
  { value: 'remedies', label: 'Remedies' },
  { value: 'bankruptcy', label: 'Bankruptcy' },
  { value: 'ip_law', label: 'Intellectual Property' },
  { value: 'fed_income_tax', label: 'Federal Income Tax' },
  { value: 'intl_law', label: 'International Law' },
  { value: 'securities_reg', label: 'Securities Regulation' },
  { value: 'unemployment_law', label: 'Unemployment Law' },
] as const;

// US states + DC for jurisdiction / target bar state capture.
// `value` is the 2-letter postal slug stored in learning_profiles.target_bar_state.
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
] as const;

// Profile summary for dashboard display
export interface ProfileSummary {
  profile: LearningProfile;
  courses: ProfileCourse[];
  topWeaknesses: ProfileBelief[];
  recentProgress: {
    sessionsThisWeek: number;
    masteryChange: number;
  };
}

// Profile belief (weakness/strength/insight/pattern)
export interface ProfileBelief {
  id: string;
  profile_id: string;
  belief_type: 'weakness' | 'strength' | 'pattern' | 'insight';
  category: string;
  subcategory: string | null;
  confidence: number;
  evidence_count: number;
  user_visible: boolean;
  display_priority: number;
  first_observed_at: string;
  last_observed_at: string;
  // Cross-feature intelligence fields
  feature_sources: string[];
  validated_across_features: boolean;
  // Historical tracking (never decays - critical for bar prep)
  historical_weakness: boolean;
  peak_weakness_confidence: number;
  // Activity tracking for staleness detection
  last_practiced_at: string | null;
  // NextGen three-axis model
  skill_domain: string | null;
  exam_format: string | null;
  // Actionable recommendation for UI
  recommendation?: string;
}

// Onboarding state
export interface OnboardingState {
  stage: 1 | 2 | 3 | 4;
  completed: boolean;
  skipped: boolean;
  answeredQuestions: string[];
  pendingQuestions: string[];
}

// Onboarding answer payload
export interface OnboardingAnswer {
  questionId: string;
  value: string | string[];
}

// Update profile payload
export interface UpdateProfilePayload {
  current_status?: CurrentStatus;
  goal_mode?: GoalMode;
  feedback_tone?: FeedbackTone;
  avg_session_length_minutes?: number;
  target_bar_state?: string | null;
  bar_attempt?: BarAttempt;
  expected_exam_date?: string | null;
  bar_exam_date?: string | null;
  school_calendar?: SchoolCalendar;
  journey_context?: string | null;
}

// Add course payload
export interface AddCoursePayload {
  subject: string;
  subject_id?: string;
  course_name?: string;
  semester?: string;
}
